// src/generators/dzial3_funkcje/temat6_zadania_uzupelniajace_maturalne/podtemat3_otwarte_types/MaturaQuadraticRevenueOpenTask.js

import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import FractionInput from '../../../../components/inputs/FractionInput';
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";


/*
  Zadanie maturalne – otwarte (0–2 pkt)

  Proste zadanie optymalizacyjne: przychód opisany funkcją kwadratową
  P(x) = (N - x)(p0 + d x), gdzie x – liczba podwyżek ceny.
*/

/* ============================================================================
 * Helpers: parsowanie liczby z FractionInput
 * ========================================================================== */

function parseNumber(rawValue) {
  if (rawValue == null) return null;

  let raw =
    typeof rawValue === 'object' && rawValue.raw != null
      ? String(rawValue.raw)
      : String(rawValue);

  raw = raw.replace(/\s+/g, '').replace(',', '.');

  if (/^-?\d+(\.\d+)?$/.test(raw)) {
    const v = parseFloat(raw);
    return Number.isFinite(v) ? v : null;
  }

  return null;
}

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/* ============================================================================
 * Scenariusze fabularne
 * ========================================================================== */

const SCENARIOS = [
  {
    id: 'hotel',
    title: 'Hotel ma do dyspozycji gości',
    capacityNoun: 'pokoi jednoosobowych',
    revenueNounGen: 'hotelu',
    priceQuestion:
      'cena wynajęcia jednoosobowego pokoju (za dobę hotelową)',
    priceUnitBullet: 'za dobę hotelową',
  },
  {
    id: 'cinema',
    title: 'Sala kinowa ma do dyspozycji',
    capacityNoun: 'miejsc na widowni',
    revenueNounGen: 'kina',
    priceQuestion: 'cena biletu na seans',
    priceUnitBullet: 'za bilet na seans',
  },
];

function getScenarioById(id) {
  return SCENARIOS.find((s) => s.id === id) || SCENARIOS[0];
}

/* ============================================================================
 * Generator parametrów funkcji kwadratowej
 * ========================================================================== */

// P(x) = (N - x)(p0 + d x)
// b = N d - p0, a = -d, wierzchołek: x0 = (N - p0/d)/2
// Wybieramy N, p0, d tak, aby x0 był ładną liczbą całkowitą.

function generateParams() {
  const d = 5; // krok ceny

  // m = p0 / d
  const mOptions = [18, 20, 22, 24]; // p0 = m * d
  const m = mOptions[randInt(0, mOptions.length - 1)];
  const p0 = m * d;

  // N - m musi być parzyste -> x0 = (N - m)/2 całkowite
  const offsetOptions = [4, 6, 8, 10, 12]; // parzyste
  const offset = offsetOptions[randInt(0, offsetOptions.length - 1)];
  const N = m + offset;

  const xOpt = offset / 2; // wierzchołek
  const priceOpt = p0 + d * xOpt;

  return { N, p0, d, xOpt, priceOpt };
}

/* ============================================================================
 * Generator zadania
 * ========================================================================== */

function createOriginalTask() {
  return {
    type: 'MaturaQuadraticRevenueOpenTask',
    data: {
      scenarioId: 'hotel',
      N: 80,
      p0: 120,
      d: 5,
      xOpt: 28,
      priceOpt: 260,
      maxPoints: 2,
      originalCke: true,
    },
    source: {
      variant: 'original',
      exam: 'MATURA',
      session: '2025_SIE',
      taskNumber: 31,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === 'original') {
    return createOriginalTask();
  }

  const { N, p0, d, xOpt, priceOpt } = generateParams();
  const scenario = SCENARIOS[randInt(0, SCENARIOS.length - 1)];

  return {
    type: 'MaturaQuadraticRevenueOpenTask',
    data: {
      scenarioId: scenario.id,
      N,
      p0,
      d,
      xOpt,
      priceOpt,
      maxPoints: 2,
    },
  };
}

/* ============================================================================
 * Treść zadania
 * ========================================================================== */

export function renderTask({ data }) {
  const { scenarioId, N, p0, d } = data;
  const scenario = getScenarioById(scenarioId);

  const domainRight = N;
  const PxLatex = `( ${N} - x )( ${p0} + ${d}x )`;

  return (
    <div className="space-y-3" style={{ textAlign: 'left' }}>
      <p>
        <strong>Zadanie maturalne – otwarte (0–2 pkt).</strong>
      </p>

      <p>
        {scenario.title}{' '}
        <InlineMath math={String(N)} />{' '}
        {scenario.capacityNoun}.
      </p>

      <p>
        Właściciel przeanalizował wpływ ceny na liczbę wynajętych
        miejsc i stwierdził, że:
      </p>

      <ul className="list-disc ml-6">
        <li>
          przy wyjściowej cenie wynoszącej{' '}
          <InlineMath math={String(p0)} /> zł{' '}
          {scenario.priceUnitBullet} wszystkie miejsca są zajęte,
        </li>
        <li>
          każdy wzrost ceny o{' '}
          <InlineMath math={String(d)} /> zł skutkuje spadkiem
          liczby zajętych miejsc o{' '}
          <InlineMath math={'1'} />.
        </li>
      </ul>

      <p>
        Przyjmijmy, że dobowy przychód{' '}
        <InlineMath math={'P'} /> {scenario.revenueNounGen} z
        wynajmowania miejsc, w zależności od liczby podwyżek ceny
        opisanej liczbą całkowitą{' '}
        <InlineMath math={'x'} />, opisuje funkcja
      </p>

      <BlockMath
        math={`P(x) = ${PxLatex}, \\quad 0 \\le x \\le ${domainRight},\\; x \\in \\mathbb{Z}.`}
      />

      <p>
        {data.originalCke ? (
          <>
            Oblicz, jaka powinna być {scenario.priceQuestion}, aby dobowy
            przychód hotelu z wynajmowania pokoi był największy. Zapisz
            obliczenia.
          </>
        ) : (
          <>
            Oblicz, przy ilu podwyżkach ceny dobowy przychód będzie największy
            oraz jaka powinna być wtedy {scenario.priceQuestion}. Zapisz
            obliczenia.
          </>
        )}
      </p>
    </div>
  );
}

/* ============================================================================
 * Input użytkownika – x i cena (oba FractionInput dla spójności)
 * ========================================================================== */

export function renderInput(_task, userAnswer, onChange, disabled) {
  const ua = userAnswer || {};
  const setField =
    (key) =>
    (val) =>
      onChange({ ...(ua || {}), [key]: val });

  return (
    <div style={{ display: 'grid', rowGap: 12, maxWidth: 360 }}>
      <OpenTaskScoringHint variant="independent" />
      <div style={{ display: 'grid', rowGap: 4 }}>
        <div>Liczba podwyżek, przy których przychód jest największy:</div>
        <div className="flex items-center gap-2">
          <FractionInput
            value={ua.x}
            onChange={setField('x')}
            disabled={disabled}
            label="x"
          />
        </div>
      </div>

      <div style={{ display: 'grid', rowGap: 4 }}>
        <div>Cena za jedno miejsce przy maksymalnym przychodzie:</div>
        <div className="flex items-center gap-2">
          <FractionInput
            value={ua.price}
            onChange={setField('price')}
            disabled={disabled}
            label="y"
            suffixLatex="\text{zł}"
          />
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * Podgląd odpowiedzi użytkownika
 * ========================================================================== */

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) {
    return <i>(brak odpowiedzi)</i>;
  }

  const rawX =
    typeof userAnswer.x === 'object'
      ? userAnswer.x?.raw ?? ''
      : String(userAnswer.x ?? '');

  const rawPrice =
    typeof userAnswer.price === 'object'
      ? userAnswer.price?.raw ?? ''
      : String(userAnswer.price ?? '');

  const xLatex = rawX || '\\text{–}';
  const priceLatex = rawPrice || '\\text{–}';

  return (
    <div style={{ display: 'grid', rowGap: 4 }}>
      <div>
        <InlineMath math={`x = ${xLatex}`} />
      </div>
      <div>
        <InlineMath math={`y = ${priceLatex} \\, \\text{zł}`} />
      </div>
    </div>
  );
}

/* ============================================================================
 * Sprawdzanie – 0–2 pkt
 * ========================================================================== */

export function checkAnswer({ data }, userAnswer) {
  const { xOpt, priceOpt, maxPoints } = data;

  const xVal = parseNumber(userAnswer?.x);
  const priceVal = parseNumber(userAnswer?.price);

  const xOk =
    xVal != null && Math.abs(xVal - xOpt) < 0.5; // liczba podwyżek – całkowita
  const priceOk =
    priceVal != null && Math.abs(priceVal - priceOpt) <= 0.5; // ±0,5 zł

  let score = 0;
  if (xOk && priceOk) {
    score = maxPoints; // 2 pkt – pełne rozwiązanie
  } else if (xOk) {
    score = 1; // 1 pkt – poprawnie wyliczona liczba podwyżek (jak w kluczu)
  } else {
    score = 0;
  }

  return {
    isCorrect: score === maxPoints,
    score,
    maxScore: maxPoints,
    parts: { xOk, priceOk },
  };
}

/* ============================================================================
 * Krótka poprawna odpowiedź
 * ========================================================================== */

export function renderAnswer({ data }) {
  const { xOpt, priceOpt } = data;

  return (
    <div style={{ textAlign: 'left', display: 'grid', rowGap: 4 }}>
      <div>
        Maksymalna liczba podwyżek:{' '}
        <InlineMath math={`x = ${xOpt}`} />
      </div>
      <div>
        Cena za jedno miejsce:{' '}
        <InlineMath math={String(priceOpt)} /> zł
      </div>
    </div>
  );
}

/* ============================================================================
 * Wyjaśnienie krok po kroku
 * ========================================================================== */

export function renderExplanation({ data }) {
  const { N, p0, d, xOpt, priceOpt } = data;

  // P(x) = -d x^2 + (Nd - p0)x + N p0
  const b = N * d - p0;

  return (
    <div className="space-y-2" style={{ textAlign: 'left' }}>
      <p>
        <strong>Krok 1.</strong> Rozpisujemy funkcję przychodu w
        postaci ogólnej funkcji kwadratowej.
      </p>
      <BlockMath
        math={`P(x) = (${N} - x)(${p0} + ${d}x) = -${d}x^2 + (${N}\\cdot${d} - ${p0})x + ${N}\\cdot${p0}.`}
      />

      <p>
        <strong>Krok 2.</strong> Współczynnik przy{' '}
        <InlineMath math={'x^2'} /> jest ujemny, więc parabola jest
        skierowana ramionami w dół. Maksymalna wartość funkcji
        odpowiada wierzchołkowi. Korzystamy ze wzoru{' '}
        <InlineMath math={'x_w = -\\dfrac{b}{2a}'} />.
      </p>

      <BlockMath
        math={`x_w = -\\frac{${b}}{2\\cdot(-${d})} = \\frac{${N} - ${
          p0 / d
        }}{2} = ${xOpt}.`}
      />

      <p>
        <strong>Krok 3.</strong> Dla{' '}
        <InlineMath math={`x = ${xOpt}`} /> cena za jedno miejsce
        wynosi{' '}
        <InlineMath
          math={`${p0} + ${d}\\cdot${xOpt} = ${priceOpt}`}
        />{' '}
        zł. Taka cena zapewnia największy dobowy przychód.
      </p>
    </div>
  );
}
